/**
 * Search Filter Utilities
 * Provides reusable search filtering functions to avoid code duplication
 */

/**
 * Filters an array of items by a search term
 * Works with objects that have a searchable text field
 * 
 * @param {Array} items - Array of items to filter
 * @param {string} searchTerm - Search term to filter by
 * @param {Function|string} getSearchableText - Function to extract searchable text from item, or property name
 * @returns {Array} Filtered array of items
 * 
 * @example
 * // Using a function
 * const filtered = filterBySearch(users, "john", (user) => user.name);
 * 
 * // Using a property name
 * const filtered = filterBySearch(users, "john", "name");
 */
export const filterBySearch = (items, searchTerm, getSearchableText) => {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  if (!searchTerm || searchTerm.trim() === '') {
    return items;
  }

  const searchLower = searchTerm.toLowerCase().trim();
  
  // If getSearchableText is a string, treat it as a property name
  if (typeof getSearchableText === 'string') {
    return items.filter((item) => {
      const text = item[getSearchableText];
      return text && typeof text === 'string' && text.toLowerCase().includes(searchLower);
    });
  }

  // If getSearchableText is a function, use it to extract text
  if (typeof getSearchableText === 'function') {
    return items.filter((item) => {
      const text = getSearchableText(item);
      return text && typeof text === 'string' && text.toLowerCase().includes(searchLower);
    });
  }

  return items;
};

/**
 * Filters users/teams by full name (first, middle, last, or company name)
 * 
 * @param {Array} users - Array of user/team objects
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered array of users
 */
export const filterUsersByFullName = (users, searchTerm) => {
  if (!users || !Array.isArray(users)) {
    return [];
  }

  if (!searchTerm || searchTerm.trim() === '') {
    return users;
  }

  const searchLower = searchTerm.toLowerCase().trim();

  return users.filter((user) => {
    const fullName = user.first_name
      ? `${user.first_name} ${user.middle_name || ''} ${user.last_name}`
      : `${user.company_first_name ? user.company_first_name + (user.company_last_name && user.company_last_name ? ' ' + user.company_last_name : '') : user.company_name || ''}`;
    
    return fullName.toLowerCase().includes(searchLower);
  });
};

/**
 * Filters payment status list by status text
 * 
 * @param {Array} paymentStatusList - Array of payment status objects
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered array of payment statuses
 */
export const filterPaymentStatusByText = (paymentStatusList, searchTerm) => {
  return filterBySearch(paymentStatusList, searchTerm, 'status');
};

